import React, { useEffect, useMemo, useCallback, useState } from "react";
import Modal from "react-modal";
import { connect } from "react-redux";

import {
  shutterbDashboardActions,
  eventBookingActions,
} from "../../../../redux/actions";
import RichTextEditor from "../../../../shared/components/Editor/RichTextEditor";
import {
  formatTextToHtml,
  getFormattedHtml,
} from "../../../../shared/utility";
import DetailRow from "./DetailRow";
import { EMPTY_STRING, SAVE } from "../../../../constants";

// Modal for showing the details of reels (recap brief)
const ReelDetailsModal = ({
  allReels,
  className,
  isModalOpen,
  handleModalClose,
  reelModalEventId,
  reelModalEventGroupId,
  getReelsByEventId,
  getReelsByEventGroupId,
  updateRecapBrief,
  isReelDescriptionEditable = false,
}) => {
  // Fetch reels when modal opens or when event/group ID changes.
  useEffect(() => {
    if (reelModalEventId) {
      getReelsByEventId(reelModalEventId);
    }
    if (reelModalEventGroupId) {
      getReelsByEventGroupId(reelModalEventGroupId);
    }
  }, [reelModalEventId, reelModalEventGroupId]);

  const primaryReels = allReels.filter((reel) => !reel.isCombinedEventsReel);
  const combinedReels = allReels.filter((reel) => reel.isCombinedEventsReel);

  const primaryDescription =
    allReels.find((reel) => !reel.isCombinedEventsReel)?.description ||
    EMPTY_STRING;

  const combinedDescription =
    allReels.find((reel) => reel.isCombinedEventsReel)?.description ||
    EMPTY_STRING;

  const initialPrimaryContent = useMemo(
    () => formatTextToHtml(primaryDescription),
    [primaryReels]
  );

  const initialCombinedContent = useMemo(
    () => formatTextToHtml(combinedDescription),
    [combinedReels]
  );

  const [currentPrimaryContent, setCurrentPrimaryContent] = useState(
    initialPrimaryContent
  );
  const [currentCombinedContent, setCurrentCombinedContent] = useState(
    initialCombinedContent
  );

  useEffect(() => {
    setCurrentPrimaryContent(initialPrimaryContent);
  }, [initialPrimaryContent]);

  useEffect(() => {
    setCurrentCombinedContent(initialCombinedContent);
  }, [initialCombinedContent]);

  const handlePrimaryContentChange = useCallback((content) => {
    setCurrentPrimaryContent(content);
  }, []);

  const handleCombinedContentChange = useCallback((content) => {
    setCurrentCombinedContent(content);
  }, []);

  const handleSave = useCallback(async () => {
    await updateRecapBrief({
      eventId: reelModalEventId,
      primaryDescription: currentPrimaryContent,
      combinedDescription: currentCombinedContent,
    });
    handleModalClose();
  }, [
    reelModalEventId,
    reelModalEventGroupId,
    currentPrimaryContent,
    currentCombinedContent,
    handleModalClose,
    updateRecapBrief,
  ]);

  return (
    <Modal
      isOpen={isModalOpen}
      className={className}
      onRequestClose={handleModalClose}
    >
      <div className="main-con">
        <div className="row">
          {/* Header */}
          <div className="row w-100 header">
            <div className="col-12">recap brief</div>
            <div className="col-12">
              {reelModalEventId || reelModalEventGroupId || 0}
            </div>
          </div>
          {/* Details */}
          <div className="w-100 my-3">
            {/* If reels exist, render them */}
            {allReels?.length > 0 && (
              <>
                {[...allReels]
                  .sort(
                    (a, b) =>
                      Number(a.isCombinedEventsReel) -
                      Number(b.isCombinedEventsReel)
                  )
                  .map((reel) => (
                    <React.Fragment key={reel.id}>
                      <DetailRow label="name:" value={reel.reelName} />
                      <DetailRow
                        label="orientation:"
                        value={reel.orientation}
                      />
                      <DetailRow label="recap type:" value={reel.reelType} />
                      <div className="row w-100">
                        <div className="col-12">
                          <hr />
                        </div>
                      </div>
                    </React.Fragment>
                  ))}

                {/* Editing Brief (Markdown + HTML Support) */}
                {primaryDescription && (
                  <>
                    <div className="row w-100">
                      <div className="col-12 col-md-4 col-lg-3 col-xl-2 py-1 py-md-2 detail-row-heading">
                        editing brief:
                      </div>
                      {isReelDescriptionEditable ? (
                        <div className="event-shot-list-editor">
                          <RichTextEditor
                            onContentChange={handlePrimaryContentChange}
                            initialContent={initialPrimaryContent}
                          />
                        </div>
                      ) : (
                        <div
                          className="col-12 col-md-8 col-lg-9 col-xl-10 py-1 py-md-2 formatted-html"
                          dangerouslySetInnerHTML={getFormattedHtml(
                            primaryDescription
                          )}
                        />
                      )}
                    </div>
                    {combinedDescription && (
                      <div className="row w-100">
                        <div className="col-12">
                          <hr />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Editing Brief for combined reels (Markdown + HTML Support) */}
                {combinedDescription && (
                  <div className="row w-100">
                    <div className="col-12 col-md-4 col-lg-3 col-xl-2 py-1 py-md-2 detail-row-heading">
                      combined editing brief:
                    </div>
                    {isReelDescriptionEditable ? (
                      <div className="event-shot-list-editor">
                        <RichTextEditor
                          onContentChange={handleCombinedContentChange}
                          initialContent={initialCombinedContent}
                        />
                      </div>
                    ) : (
                      <div
                        className="col-12 col-md-8 col-lg-9 col-xl-10 py-1 py-md-2 formatted-html"
                        dangerouslySetInnerHTML={getFormattedHtml(
                          combinedDescription
                        )}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Save Button */}
        {allReels?.length > 0 && isReelDescriptionEditable && (
          <div className="d-flex justify-content-center w-100 mt-3">
            <button
              type="button"
              className="button-common button-fill"
              onClick={handleSave}
            >
              {SAVE}
            </button>
          </div>
        )}

        {/* Close Button */}
        <button onClick={handleModalClose} className="modal-close">
          <img
            alt="crossIcon"
            className="modal-close-img"
            src="/images/simpleCrossWhite.svg"
          />
        </button>
      </div>
    </Modal>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  allReels: state.shutterbDashboard.allReels,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getReelsByEventId: (eventId) =>
    dispatch(shutterbDashboardActions.getReelsByEventId(eventId)),
  getReelsByEventGroupId: (eventGroupId) =>
    dispatch(shutterbDashboardActions.getReelsByEventGroupId(eventGroupId)),
  updateRecapBrief: (data) =>
    dispatch(eventBookingActions.updateRecapBrief(data)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(ReelDetailsModal);
